/**
 * The SDK's friend doors onto a `SheetHandle` — what sibling SDK modules, the
 * SDK's own white-box tests and the in-page harness reach and an application
 * never does. No barrel re-exports this module whole: the workspace-only
 * `@tento-lona/internal-testing` re-exports the instrumentation half and
 * installs its in-page door; the rest is `tento-lona-js/src/` only
 * (the boundary lint keeps every other package off it; `jsr.json` publishes
 * no path to it).
 *
 * The doors are registered once by `SheetHandle`'s static block — the only
 * code that reaches the handle's private members — so this module imports
 * nothing from `client.ts` at runtime and evaluates before it.
 */

import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import type { ModelRow, RowLocalId, RowNetworkKey, SystemRowDef } from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";
import type { IngestionGate } from "@tento-lona/sheets/platform";
import type {
  ListenerDrainObserver as RowsListenerDrainObserver,
  RecomputeSweepObserver as RowsRecomputeSweepObserver,
  SourceInstallObserver as RowsSourceInstallObserver,
  StoreInstallObserver as RowsStoreInstallObserver,
} from "@tento-lona/sheets/internal-sdk";

import type { SheetModelCache } from "@tento-lona/sheets/internal-sdk";

import type { EphemeralSheets } from "../client/ephemeral-spec.ts";
import type { SheetHandle } from "../client/sheet-handle.ts";
import type { SheetId } from "@tento-lona/sheets";
import type { SheetKey } from "../client/sheet-key.ts";
import type { SheetsClient } from "../services/sheets/sheets-client.ts";
import type { CellAccessor } from "../cells/sheet-cell-accessor.ts";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import type { SheetRow } from "../rows/sheet-row.ts";
import type { RowNode, Unresolved } from "../rows/row-node.ts";

/**
 * The wiring a sibling SDK module needs from a `SheetHandle` and a consumer
 * never does: the full cell accessor behind the narrow `handle.cells`, the
 * chrome (system-row) swap and the formula-graph rebuild. Returned by
 * {@link sheetHandleInternals}.
 *
 * Reached by
 * `SheetViewModel` (chrome rebuilds) and the SDK's own white-box tests.
 * (`RowAccessor` gets its two doors — root insert with an actor, cell-cache
 * eviction — injected at construction instead: `RowAccessor.Wiring`.)
 */
export interface SheetHandleInternals {
  /** The full cell accessor; `handle.cells` is the consumer-facing slice of it. */
  readonly cells: CellAccessor;
  /** Swap the chrome in the joined-side tree (one `generation` bump). */
  replaceSystemRows(defs: SheetHandle.SystemRowPlacement[]): void;
  /** Rebuild the chrome from the current rows; an identical rebuild is free. */
  rebuildSystemRows(config: Option<SheetHandle.RebuildConfig>): void;
  /** The rows-side half of a chrome swap, without the structural echo bump. */
  mirrorSystemRowsToRowsSheet(rows: readonly SystemRowDef[]): void;
  /** Rebuild the formula dependency graph from the row registry. */
  rebuildGraph(): void;
  /** The row tree before resolution — the base a pending move starts from. */
  unresolvedTree(): RowNode.Tree<typeof Unresolved>;
  /** Take one more hold for an open that returns this cached handle (see
   *  `SheetHandle.dispose`); throws `SheetHandle.DisposedError` once torn down. */
  retain(): void;
  /** Tear the handle down whatever its holds. */
  teardown(): void;
  /**
   * The full rows-side `Sheet` behind the narrow `handle.model` view — for
   * `SheetViewModel` (timescale sync) and the SDK modules that write the
   * rows-side model (row mirrors, attribute patches, the cell fan-out).
   */
  readonly sheet: Sheet;
}

/**
 * What `instrumentationOf(handle)` (`@tento-lona/internal-testing`) hands a test
 * harness: live objects, not copies — the full rows-side sheet and its rows,
 * the cell accessor's row handles and the two ingestion gates, which a harness
 * may wrap or patch (the ux-test sampler wraps `receiveBatchData` and awaits
 * `whenReleased`), plus typed observers for the rows-side installs, recompute
 * sweeps and listener drains. Patching them changes how the sheet behaves.
 * `rows` and `rowHandles` re-read the live collections on every access.
 *
 * Re-exported by the workspace-only `@tento-lona/internal-testing`.
 */
export interface SheetInstrumentation {
  /**
   * The full rows-side `Sheet` the handle renders from, for harnesses (and the
   * CLI's drivers) that drive it directly: fixture ops, formula recompute,
   * scene builds. `handle.model` is the read-only view of the same object.
   */
  readonly sheet: Sheet;
  /** Every row of the rows-side sheet, tree order (`Sheet.allRows()`). */
  readonly rows: Iterable<ModelRow>;
  /** Every row handle the cell accessor currently holds. */
  readonly rowHandles: Iterable<RowCellCache>;
  /**
   * The row handle for a row id (any spelling — it resolves to the row's
   * cell-access key), minted on first use: the handle every reader of that
   * row shares.
   */
  rowHandle(key: string): RowCellCache;
  /** The row handle already held for `key`, or `null`; never mints one. */
  peekRowHandle(key: string): Option<RowCellCache>;
  /** The response-install gates of the rows-side sheet and the cell accessor, and their observers. */
  readonly ingestion: {
    readonly rows: IngestionGate.Waiter;
    readonly cells: IngestionGate.Waiter;
    /**
     * Observe every batch install on the rows-side sheet's source rows
     * (the package-private `installBatchedCells`, `since` and plain path
     * alike): one event per install, before it runs, with the row, range
     * and cell count. A returned function runs after the install (thrown
     * or not). Returns the unsubscribe.
     */
    onSourceInstall(observer: RowsSourceInstallObserver): Unsubscribe;
    /**
     * Observe every server-snapshot install bracket on a rows-side cell
     * store — `"canonical"` (the client-shared `:~` store) or `"local"` (the
     * sheet's own store) — before it runs. A returned function runs after
     * the install. Returns the unsubscribe.
     */
    onStoreInstall(
      store: "canonical" | "local",
      observer: RowsStoreInstallObserver,
    ): Unsubscribe;
    /** Observe every recompute sweep over the rows-side computed sources (with its range). */
    onRecomputeSweep(observer: RowsRecomputeSweepObserver): Unsubscribe;
    /** Observe every drain of the rows-side sheet's deferred cell-listener work. */
    onListenerDrain(observer: RowsListenerDrainObserver): Unsubscribe;
  };
}

/** What `SheetHandle`'s static block registers: the two private views and the prototype. */
export interface HandleDoors {
  internalsOf(handle: SheetHandle): SheetHandleInternals;
  instrumentationOf(handle: SheetHandle): SheetInstrumentation;
  /** `SheetHandle.prototype`, for the opt-in harness door. */
  readonly prototype: SheetHandle;
}

let doors: Option<HandleDoors> = null;

/** Called once, from `SheetHandle`'s static block. */
export function registerHandleDoors(registered: HandleDoors): void {
  if (doors != null) throw new Error("registerHandleDoors: the SheetHandle doors are registered once");
  doors = registered;
}

function handleDoors(): HandleDoors {
  return nonnull(doors, "internal/doors: SheetHandle has not registered its doors (import ../client/client first)");
}

/**
 * The runtime door a browser harness (the ux-test samplers) reaches
 * `instrumentationOf` through, since it cannot import `@tento-lona/internal-testing`
 * into the app bundle:
 * `handle[Symbol.for("@tento-lona/testing:sheetInstrumentation")]()`.
 * Non-enumerable and on no typed surface. Opt-in: installed only by
 * {@link installSheetInstrumentationDoor} (`installHarnessDoors` on the
 * workspace-only `@tento-lona/internal-testing`, `installUiHarnessDoors` on
 * `@tento-lona/sheets-ui/internal-testing`), never by loading the SDK, and by
 * no published entry.
 */
const SHEET_INSTRUMENTATION_KEY = Symbol.for("@tento-lona/testing:sheetInstrumentation");

/**
 * Install the `Symbol.for` harness door onto `SheetHandle.prototype`
 * (idempotent). Called by `installHarnessDoors`
 * (`@tento-lona/internal-testing`).
 */
export function installSheetInstrumentationDoor(): void {
  const { prototype, instrumentationOf } = handleDoors();
  if (Object.prototype.hasOwnProperty.call(prototype, SHEET_INSTRUMENTATION_KEY)) return;
  Object.defineProperty(prototype, SHEET_INSTRUMENTATION_KEY, {
    value: function instrumentationDoor(this: SheetHandle): SheetInstrumentation {
      return instrumentationOf(this);
    },
  });
}

/**
 * The white-box view of a client's rows side: the full rows-side sheets its
 * platform door (`platformOf(client)`) hands out only as their reads, and the
 * transaction log's recorder (`client.txLog` is its reads). LIVE objects:
 * writing through them changes the client's local model with no request and
 * no verdict. Tests import it as `clientInstrumentationOf` from the
 * workspace-only `@tento-lona/internal-testing`.
 */
export interface ClientInstrumentation {
  /** `platformOf(client).ids` whole: the client's `RowLocalId` registry, writers included. */
  readonly ids: RowLocalId.Registry;
  /** `platformOf(client).canonicalCells` whole: the canonical cell store, writers included. */
  readonly canonicalCells: SheetModelCache["canonicalCells"];
  /** The client's ordinary (row-local-id keyed) cell store, writers included. */
  readonly ordinaryCells: SheetModelCache["ordinaryCells"];
  /** The client's row-identity ledger (its nodes and versions), shared by every sheet it opens. */
  readonly ledger: SheetModelCache["ledger"];
  /**
   * Row-identity stage 2's switch, off by default: while `on`, a pending
   * draft's promote sends the draft's client-minted row id
   * (`PendingRowPersist`; requests/server-to-sdk-stage1-1).
   */
  readonly clientRowIds: { on: boolean };
  /** The client's sheet-list cache writers (seed it as a server read would). */
  readonly sheetList: SheetsClient.CacheWriters;
  /** `platformOf(client).openSheet(key)`'s sheet, whole. */
  openSheet(key: SheetKey | SheetId): Promise<Sheet>;
  /** `platformOf(client).ephemeralSheet(spec)`'s sheet, whole. */
  ephemeralSheet(spec: EphemeralSheets.EphemeralSheet): Sheet;
  /** `platformOf(client).peekSheet(key)`'s sheet, whole. */
  peekSheet(key: SheetKey | SheetId): Option<Sheet>;
  /** The rows-side transaction log with its recorder. */
  readonly txLog: SheetModelCache["txLog"];
  /**
   * The client's live row registry (`client.rowRegistry` hands out its rows
   * as frozen reads faces): the live `SheetRow`s, their wire records
   * (`raw(id)`) and the registry's writers.
   */
  readonly rowRegistry: SheetRow.Registry;
}

/**
 * The test-harness seam onto `handle` — see {@link SheetInstrumentation}.
 * Tests import it as `instrumentationOf` from `@tento-lona/internal-testing`.
 */
export function sheetInstrumentationOf(handle: SheetHandle): SheetInstrumentation {
  return handleDoors().instrumentationOf(handle);
}

/**
 * The SDK-private wiring door onto `handle` — see {@link SheetHandleInternals}.
 * The whole view is for tests; a production module takes the typed port it
 * needs (`sheetChromeOf`, `rowsSideSheetOf`, `sheetRowCacheOf`), so each says
 * what it reaches.
 */
export function sheetHandleInternals(handle: SheetHandle): SheetHandleInternals {
  return handleDoors().internalsOf(handle);
}

/**
 * What `SheetViewModel` changes on a handle's chrome: the system-row swap
 * (both halves, one `generation` bump), a rebuild, and the rows-side sheet's
 * timescale.
 */
export interface SheetChromePort {
  replaceSystemRows(defs: SheetHandle.SystemRowPlacement[]): void;
  rebuildSystemRows(config: Option<SheetHandle.RebuildConfig>): void;
  mirrorSystemRowsToRowsSheet(rows: readonly SystemRowDef[]): void;
  setTimescale(timescale: Parameters<Sheet["setTimescale"]>[0]): void;
}

/** `handle`'s chrome port (`SheetChromePort`). */
export function sheetChromeOf(handle: SheetHandle): SheetChromePort {
  const internals = handleDoors().internalsOf(handle);
  return {
    replaceSystemRows: (defs) => internals.replaceSystemRows(defs),
    rebuildSystemRows: (config) => internals.rebuildSystemRows(config),
    mirrorSystemRowsToRowsSheet: (rows) => internals.mirrorSystemRowsToRowsSheet(rows),
    setTimescale: (timescale) => internals.sheet.setTimescale(timescale),
  };
}

/** The rows-side `Sheet` behind `handle`, for the SDK module that writes the rows-side model (`SheetDataSource`). */
export function rowsSideSheetOf(handle: SheetHandle): Sheet {
  return handleDoors().internalsOf(handle).sheet;
}

/** `handle`'s cell cache for the row `key`, minted on first use as a cell read would (`eventsTableOf`). */
export function sheetRowCacheOf(handle: SheetHandle, key: RowNetworkKey): RowCellCache {
  return handleDoors().internalsOf(handle).cells.rowHandle(key);
}
